import DOMPurify from 'dompurify';
import { LoaderFunctionArgs, redirect } from 'react-router';

import type { RouteHandle } from '@router/routeHandle';
import { defaultLanguage } from '@services/defaultLanguage';
import { remoteApi } from '@services/remoteApi';
import { resolveAuthRoute, type AuthRouteType } from '@services/resolveAuthRoute';
import { userStateBridge } from '@services/userStateBridge';

const GUARD_REDIRECT_PATHS: Record<string, string> = {
  'Admin Welcome': '/:locale/welcome',
  'Admin Login': '/:locale/login',
  'Admin Login Breakglass': '/:locale/login-breakglass',
  'Admin 401 Page': '/:locale/not-authorized-401',
  '404 Page': '/:locale/not-found-404',
};

const sanitizeParams = (params: Record<string, string | undefined>): Record<string, string> =>
  Object.fromEntries(Object.entries(params).map(([key, value]) => [key, DOMPurify.sanitize(value ?? '')]));

const routeIdToPath = (name: string, locale: string): string => {
  const pattern = GUARD_REDIRECT_PATHS[name] ?? '/:locale/welcome';
  return pattern.replace(':locale', locale);
};

const redirectToAuthRoute = (type: AuthRouteType, locale: string, path: string, url: URL) => {
  const target = resolveAuthRoute(type, {
    locale,
    path,
    area: 'admin',
    query: {
      error: url.searchParams.get('error') ?? undefined,
      error_description: url.searchParams.get('error_description') ?? undefined,
    },
  });

  const search = new URLSearchParams(
    Object.entries(target.query).filter((entry): entry is [string, string] => entry[1] != null),
  ).toString();

  return redirect(`${routeIdToPath(target.name, locale)}${search ? `?${search}` : ''}`);
};

const createRouteGuardLoader =
  (_routeId: string, handle: RouteHandle) =>
  async ({ params, request }: LoaderFunctionArgs) => {
    const cleanParams = sanitizeParams(params);
    const url = new URL(request.url);
    const locale = cleanParams.locale || defaultLanguage.get();

    const requiresAuth = handle.auth !== undefined && handle.auth !== false;
    if (!requiresAuth) return null;

    if (!userStateBridge.isAuthenticated()) {
      try {
        await remoteApi.refreshSelf();
      } catch {
        return redirectToAuthRoute('login', locale, url.pathname, url);
      }
    }

    if (!remoteApi.validateRoles(handle.auth)) {
      return redirectToAuthRoute('401', locale, url.pathname, url);
    }

    return null;
  };

export { createRouteGuardLoader, GUARD_REDIRECT_PATHS, routeIdToPath };
